export const RELIABLE_TOUCH_MOVE_THRESHOLD_PX = 8
export const COMPATIBILITY_CLICK_SUPPRESSION_MS = 750

export interface TouchPointLike {
    identifier: number
    clientX: number
    clientY: number
}

export interface ReliableTouchGesture {
    identifier: number
    startX: number
    startY: number
    moved: boolean
}

type TouchListLike = ArrayLike<TouchPointLike>

function findTouch(touches: TouchListLike, identifier: number): TouchPointLike | null {
    for (let index = 0; index < touches.length; index += 1) {
        const touch = touches[index]
        if (touch?.identifier === identifier) return touch
    }

    return null
}

function movedPastThreshold(gesture: ReliableTouchGesture, touch: TouchPointLike): boolean {
    return (
        Math.hypot(touch.clientX - gesture.startX, touch.clientY - gesture.startY) >
        RELIABLE_TOUCH_MOVE_THRESHOLD_PX
    )
}

export function beginReliableTouchGesture(touches: TouchListLike): ReliableTouchGesture | null {
    if (touches.length !== 1) return null

    const touch = touches[0]
    if (!touch) return null

    return {
        identifier: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        moved: false,
    }
}

export function updateReliableTouchGesture(
    gesture: ReliableTouchGesture | null,
    touches: TouchListLike,
): ReliableTouchGesture | null {
    if (!gesture || touches.length !== 1) return null

    const touch = findTouch(touches, gesture.identifier)
    if (!touch) return null

    if (movedPastThreshold(gesture, touch)) {
        gesture.moved = true
    }

    return gesture
}

export function shouldActivateReliableTouchGesture(
    gesture: ReliableTouchGesture | null,
    changedTouches: TouchListLike,
    remainingTouchCount = 0,
): boolean {
    if (!gesture || remainingTouchCount !== 0 || changedTouches.length !== 1) return false

    const touch = findTouch(changedTouches, gesture.identifier)
    if (!touch) return false

    return !gesture.moved && !movedPastThreshold(gesture, touch)
}

export function shouldSuppressCompatibilityClick({
    clickDetail,
    dispatchingReliableClick,
    now,
    suppressUntil,
}: {
    clickDetail: number
    dispatchingReliableClick: boolean
    now: number
    suppressUntil: number
}): boolean {
    // Keyboard and assistive-technology clicks conventionally have detail=0 and must retain
    // native behavior even if they happen shortly after a touch gesture.
    return !dispatchingReliableClick && clickDetail !== 0 && now < suppressUntil
}
